import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress
} from '@mui/material';
import { DragIndicator, Search } from '@mui/icons-material';
import { ReportType, ReportField } from '../../pages/Reports/Reports';
import { AvailableFieldsResponse } from '../../services/reportsApi';
import { useTranslation } from 'react-i18next';
import styles from './FieldSelector.module.css';

interface FieldSelectorProps {
  reportType: ReportType;
  fields: ReportField[];
  availableFieldsData: AvailableFieldsResponse | null;
  onChange: (fields: ReportField[]) => void;
  loading?: boolean;
}

const FieldSelector: React.FC<FieldSelectorProps> = ({
  reportType,
  fields,
  availableFieldsData,
  onChange,
  loading = false
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTables, setSelectedTables] = useState<Set<string>>(new Set());
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Get unique tables from fields
  const availableTables = useMemo(() => {
    const tables = new Set<string>();
    fields.forEach(field => {
      tables.add(field.table);
    });
    return Array.from(tables);
  }, [fields]);

  // Filter fields based on search and table filters
  const filteredFields = useMemo(() => {
    let filtered = fields;

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(field =>
        field.label.toLowerCase().includes(query) ||
        field.tableLabel.toLowerCase().includes(query)
      );
    }

    // Filter by selected tables
    if (selectedTables.size > 0) {
      filtered = filtered.filter(field => selectedTables.has(field.table));
    }

    return filtered;
  }, [fields, searchQuery, selectedTables]);

  const handleToggleField = (fieldId: string) => {
    const newFields = fields.map(field =>
      field.id === fieldId ? { ...field, selected: !field.selected } : field
    );
    onChange(newFields);
  };

  const handleToggleTable = (table: string) => {
    const newSelectedTables = new Set(selectedTables);
    if (newSelectedTables.has(table)) {
      newSelectedTables.delete(table);
    } else {
      newSelectedTables.add(table);
    }
    setSelectedTables(newSelectedTables);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
      // Find actual indices in original fields array
      const draggedField = filteredFields[draggedIndex];
      const dragOverField = filteredFields[dragOverIndex];

      const draggedOriginalIndex = fields.findIndex(f => f.id === draggedField.id);
      const dragOverOriginalIndex = fields.findIndex(f => f.id === dragOverField.id);

      if (draggedOriginalIndex !== -1 && dragOverOriginalIndex !== -1) {
        const newFields = [...fields];
        const [removed] = newFields.splice(draggedOriginalIndex, 1);
        newFields.splice(dragOverOriginalIndex, 0, removed);
        onChange(newFields);
      }
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleDragEnd();
  };

  // Clear filters when fields change
  useEffect(() => {
    setSearchQuery('');
    setSelectedTables(new Set());
  }, [reportType]);

  if (loading) {
    return (
      <Box className={styles.container}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Typography variant="h6" className={styles.label}>
        {t('reports.selectFields')}
      </Typography>

      {/* Search */}
      <TextField
        fullWidth
        placeholder={t('reports.searchFields')}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className={styles.search}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
      />

      {/* Table filters */}
      {availableTables.length > 1 && (
        <Box className={styles.tableFilters}>
          <Typography variant="body2" className={styles.filterLabel}>
            {t('reports.filterByTables')}
          </Typography>
          <Box className={styles.chipsContainer}>
            {availableTables.map(table => {
              const tableLabel = fields.find(f => f.table === table)?.tableLabel || table;
              const isSelected = selectedTables.size === 0 || selectedTables.has(table);
              return (
                <Chip
                  key={table}
                  label={tableLabel}
                  onClick={() => handleToggleTable(table)}
                  color={isSelected ? 'primary' : 'default'}
                  variant={isSelected ? 'filled' : 'outlined'}
                  className={styles.tableChip}
                />
              );
            })}
          </Box>
        </Box>
      )}

      {/* Fields list */}
      <Paper className={styles.fieldsList}>
        {filteredFields.length === 0 ? (
          <Typography variant="body2" className={styles.noResults}>
            {t('reports.noResults')}
          </Typography>
        ) : (
          filteredFields.map((field, index) => (
            <Box
              key={field.id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              onDrop={handleDrop}
              className={`${styles.fieldItem} ${draggedIndex === index ? styles.dragging : ''
                } ${dragOverIndex === index ? styles.dragOver : ''}`}
            >
              <DragIndicator className={styles.dragHandle} />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.selected}
                    onChange={() => handleToggleField(field.id)}
                    className={styles.checkbox}
                  />
                }
                label={
                  <Box className={styles.fieldLabel}>
                    <Typography variant="body2" className={styles.fieldName}>
                      {field.label}
                    </Typography>
                    {availableTables.length > 1 && (
                      <Typography variant="caption" className={styles.tableName}>
                        {field.tableLabel}
                      </Typography>
                    )}
                  </Box>
                }
                className={styles.label}
              />
            </Box>
          ))
        )}
      </Paper>

      <Typography variant="body2" className={styles.hint}>
        {t('reports.fieldsOrderHint')}
      </Typography>
    </Box>
  );
};

export default FieldSelector;
